import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);
const PX_PER_HOUR = 20;
const ROW_H = 56;

const toISO = (d) => d.toISOString();
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const fmtDay = (d) => d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function SchedulePage() {
  const [weekStart, setWeekStart] = useState(() => {
    const d = startOfDay(new Date()); d.setDate(d.getDate() - d.getDay() + 1); return d;
  });
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const fromISO = toISO(weekStart).slice(0, 10);
  const toISOStr = toISO(addDays(weekStart, 6)).slice(0, 10);
  const { data, mutate: refresh } = useSWR(`/schedule?from_date=${fromISO}&to_date=${toISOStr}`, fetcher, { refreshInterval: 15000 });
  const { data: moulds } = useSWR("/moulds", fetcher);
  const { data: allMachines } = useSWR("/machines", fetcher);
  const [edit, setEdit] = useState(null);

  const machines = data?.machines || [];
  const orders = data?.orders || [];
  const util = data?.utilization_pct || {};
  const mouldMap = useMemo(() => Object.fromEntries((moulds||[]).map(m => [m.mould_id, m])), [moulds]);
  const schedulableMachineIds = useMemo(() => new Set((allMachines||[])
    .filter(m => !["COMPRESSOR","CHILLER","OTHER"].includes(m.type))
    .map(m => m.machine_id)), [allMachines]);

  const byUnit = useMemo(() => {
    const g = {};
    machines.forEach(m => {
      const k = m.unit_id || "_"; const name = m.unit_name || "No Unit";
      if (!g[k]) g[k] = { name, machines: [] };
      g[k].machines.push(m);
    });
    return g;
  }, [machines]);

  const totalDays = 7;
  const timelineWidth = totalDays * 24 * PX_PER_HOUR;

  const blockGeom = (o) => {
    const s = new Date(o.planned_start).getTime();
    const e = new Date(o.planned_end).getTime();
    const left = Math.max(0, (s - weekStart.getTime()) / 36e5 * PX_PER_HOUR);
    const width = Math.max(20, (e - s) / 36e5 * PX_PER_HOUR);
    return { left, width };
  };

  const onDragStart = (ev, o) => {
    ev.dataTransfer.setData("text/plain", JSON.stringify({
      po_id: o.po_id, duration_ms: new Date(o.planned_end) - new Date(o.planned_start),
    }));
    ev.dataTransfer.effectAllowed = "move";
  };
  const onDrop = async (ev, machine_id) => {
    ev.preventDefault();
    const data = JSON.parse(ev.dataTransfer.getData("text/plain") || "{}");
    if (!data.po_id) return;
    const rowRect = ev.currentTarget.getBoundingClientRect();
    const x = ev.clientX - rowRect.left;
    const hourFromStart = Math.max(0, Math.round(x / PX_PER_HOUR));
    const start = new Date(weekStart.getTime() + hourFromStart * 36e5);
    const end = new Date(start.getTime() + (data.duration_ms || 3600000));
    try {
      await api.patch(`/production-orders/${data.po_id}/schedule`, {
        planned_machine_id: machine_id,
        planned_start: start.toISOString(), planned_end: end.toISOString(),
      });
      toast.success("Scheduled");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const onDragOver = (ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = "move"; };

  const saveEdit = async () => {
    try {
      await api.patch(`/production-orders/${edit.po_id}/schedule`, {
        planned_machine_id: edit.planned_machine_id,
        planned_mould_id: edit.planned_mould_id,
        planned_start: edit.planned_start, planned_end: edit.planned_end,
        efficiency: Number(edit.efficiency || 0.85),
      });
      toast.success("Updated");
      setEdit(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarDays size={22} className="text-sky-600"/> Production Schedule</h1>
          <p className="text-sm text-slate-500">Drag an order to another machine or time slot. Mould / machine compatibility and overlaps are flagged.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-week-prev" onClick={()=>setWeekStart(addDays(weekStart, -7))} className="p-1.5 border border-slate-200 rounded hover:bg-slate-50"><ChevronLeft size={14}/></button>
          <div className="text-sm font-medium text-slate-700" data-testid="schedule-window">{fmtDay(weekStart)} → {fmtDay(addDays(weekStart,6))}</div>
          <button data-testid="btn-week-next" onClick={()=>setWeekStart(addDays(weekStart, 7))} className="p-1.5 border border-slate-200 rounded hover:bg-slate-50"><ChevronRight size={14}/></button>
          <button data-testid="btn-week-today" onClick={()=>{ const d = startOfDay(new Date()); d.setDate(d.getDate() - d.getDay() + 1); setWeekStart(d); }} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50">Today</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto">
          <div style={{minWidth: 220 + timelineWidth}}>
            {/* Header row */}
            <div className="flex sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
              <div className="w-56 shrink-0 px-3 py-2 text-xs font-semibold uppercase text-slate-500 border-r border-slate-200">Machine</div>
              <div className="relative" style={{width: timelineWidth, height: 32}}>
                {Array.from({length: totalDays}).map((_, i) => (
                  <div key={i} className="absolute top-0 bottom-0 border-r border-slate-200 flex items-center px-2 text-xs text-slate-600"
                       style={{left: i * 24 * PX_PER_HOUR, width: 24 * PX_PER_HOUR}}>
                    {fmtDay(addDays(weekStart, i))}
                  </div>
                ))}
              </div>
            </div>

            {Object.entries(byUnit).map(([uid, g]) => (
              <div key={uid} data-testid={`schedule-unit-${uid}`}>
                <div className="bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase text-slate-600 tracking-wider border-b border-slate-200">{g.name}</div>
                {g.machines.map(m => {
                  const rowOrders = orders.filter(o => o.planned_machine_id === m.machine_id);
                  return (
                    <div key={m.machine_id} className="flex border-b border-slate-100" data-testid={`schedule-row-${m.machine_id}`}>
                      <div className="w-56 shrink-0 px-3 py-2 border-r border-slate-200 bg-white">
                        <div className="text-sm font-medium text-slate-900">{m.name}</div>
                        <div className="text-[10px] text-slate-500 flex gap-2">
                          <span className="font-mono">{m.code}</span>
                          <span>{m.type}</span>
                          {util[m.machine_id] != null && (
                            <span className={`font-mono ${util[m.machine_id] > 90 ? "text-red-600" : util[m.machine_id] > 60 ? "text-amber-600" : "text-emerald-700"}`}
                                  data-testid={`util-${m.machine_id}`}>util {util[m.machine_id]}%</span>
                          )}
                        </div>
                      </div>
                      <div className="relative bg-slate-50" style={{width: timelineWidth, height: ROW_H}}
                           onDragOver={onDragOver} onDrop={e=>onDrop(e, m.machine_id)} data-testid={`drop-zone-${m.machine_id}`}>
                        {Array.from({length: totalDays}).map((_, i) => (
                          <div key={i} className="absolute top-0 bottom-0 border-r border-slate-200"
                               style={{left: i * 24 * PX_PER_HOUR, width: 24 * PX_PER_HOUR}}/>
                        ))}
                        {rowOrders.map(o => {
                          const { left, width } = blockGeom(o);
                          const warn = o.has_overlap || !o.mould_compatible;
                          return (
                            <div key={o.po_id} draggable onDragStart={e=>onDragStart(e, o)}
                                 onClick={()=>setEdit({...o})}
                                 title={`${o.po_no} · ${o.product_name} · ${o.required_hours||0}h` + (warn ? ` · ${o.has_overlap ? "overlap!" : "mould not compatible"}` : "")}
                                 data-testid={`block-${o.po_id}`}
                                 className={`absolute top-1 bottom-1 rounded px-2 py-1 text-[11px] text-white cursor-move select-none overflow-hidden shadow-sm ${warn ? "bg-red-600 border border-red-800" : "bg-sky-600 hover:bg-sky-700"}`}
                                 style={{left, width}}>
                              <div className="font-mono truncate">{o.po_no}</div>
                              <div className="truncate">{o.product_name}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {machines.length === 0 && <div className="text-center text-slate-400 py-10 text-sm">No schedulable machines. Add moulding machines in Masters → Machines.</div>}
            {machines.length > 0 && orders.length === 0 && <div className="text-center text-slate-400 py-6 text-sm">No orders scheduled in this window. Open any Production Order to assign planned start / end.</div>}
          </div>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
        <span><span className="inline-block w-3 h-3 bg-sky-600 rounded-sm align-middle mr-1"/>OK</span>
        <span><span className="inline-block w-3 h-3 bg-red-600 rounded-sm align-middle mr-1"/>Overlap or mould-machine incompatible</span>
        <span>· Excludes {(data?.excluded_machine_types||["CHILLER","COMPRESSOR","OTHER"]).join(" / ")} machines</span>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-xl p-5" data-testid="schedule-edit-modal">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">{edit.po_no}</h3>
                <div className="text-xs text-slate-500">{edit.product_name} · planned {edit.planned_qty}</div>
              </div>
              <button onClick={()=>setEdit(null)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Machine</label>
                <select data-testid="input-sched-machine" value={edit.planned_machine_id || ""} onChange={e=>setEdit({...edit, planned_machine_id: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  {(allMachines||[]).filter(m => schedulableMachineIds.has(m.machine_id)).map(m => <option key={m.machine_id} value={m.machine_id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Mould</label>
                <select data-testid="input-sched-mould" value={edit.planned_mould_id || ""} onChange={e=>setEdit({...edit, planned_mould_id: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="">—</option>
                  {(moulds||[]).map(m => <option key={m.mould_id} value={m.mould_id}>{m.name} ({m.cavity||0} cav · {m.cycle_time_sec||0}s)</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Planned start</label>
                <input data-testid="input-sched-start" type="datetime-local" value={(edit.planned_start||"").slice(0,16)} onChange={e=>setEdit({...edit, planned_start: new Date(e.target.value).toISOString()})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Planned end</label>
                <input data-testid="input-sched-end" type="datetime-local" value={(edit.planned_end||"").slice(0,16)} onChange={e=>setEdit({...edit, planned_end: new Date(e.target.value).toISOString()})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Efficiency (0-1)</label>
                <input data-testid="input-sched-eff" type="number" step="0.05" min="0.1" max="1" value={edit.efficiency||0.85} onChange={e=>setEdit({...edit, efficiency: e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500 font-semibold">Required hours</label>
                <div className="text-sm py-1.5 font-mono" data-testid="sched-required-hours">{edit.required_hours || 0}</div>
              </div>
            </div>
            {edit.planned_mould_id && mouldMap[edit.planned_mould_id] && (() => {
              const compat = mouldMap[edit.planned_mould_id].compatible_machines || [];
              const ok = !compat.length || compat.includes(edit.planned_machine_id);
              return !ok && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800 flex gap-2" data-testid="compat-warning">
                  <AlertTriangle size={14}/> This mould is only compatible with: {compat.map(c => (allMachines||[]).find(m=>m.machine_id===c)?.name || c).join(", ") || "—"}.
                </div>
              );
            })()}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setEdit(null)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-save-schedule" onClick={saveEdit} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
